import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumbs, Typography, Link, Box } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';

interface NavigationState {
  history: Array<{
    path: string;
    title: string;
    timestamp: number;
    state?: any;
  }>;
  currentIndex: number;
}

interface NavigationContextType {
  navigateWithHistory: (path: string, title: string, state?: any) => void;
  createNavigationHandler: (path: string, title?: string, state?: any) => (e: React.MouseEvent) => void;
  goBack: () => void;
  canGoBack: boolean;
  getBreadcrumbs: () => Array<{ label: string; path?: string }>;
  preservePageState: (key: string, state: any) => void;
  getPageState: (key: string) => any;
  clearPageState: (key?: string) => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

const routeTitles: Record<string, string> = {
  '/': 'Dashboard',
  '/inventory': 'Inventory',
  '/inventory/add': 'Add Vehicle',
  '/inventory-basic': 'Basic Inventory',
  '/customers': 'Customers',
  '/sales': 'Sales',
  '/sales/new': 'New Sale',
  '/sales/add': 'New Sale',
  '/workshop': 'Workshop',
  '/intelligence': 'Intelligence Hub',
  '/commission': 'Commission',
  '/settings': 'Settings',
  '/documents': 'Documents',
  '/reports': 'Reports',
  '/standard-reports': 'Standard Reports',
  '/management-reports': 'Management Reports',
  '/tasks': 'Tasks',
  '/calendar': 'Calendar',
  '/marketing': 'Marketing',
  '/accounting': 'Accounting',
  '/getting-started': 'Getting Started',
  '/profile': 'User Profile',
  '/help': 'Help & Support',
  '/tips-and-posts': 'Tips & Posts',
  '/migrations': 'Data Migration',
  '/migrations/virtualyard': 'VirtualYard Migration',
  '/migrations/easycars': 'EasyCars Migration',
  '/migrations/excel': 'Excel Import',
  '/ai': 'AI Assistant',
  '/leads': 'Leads',
  '/sourcing': 'Vehicle Sourcing',
  '/advertising': 'Advertising',
  '/ppsr-reports': 'PPSR/Car History',
  '/expenses': 'Expenses',
  '/overhead-expenses': 'Overhead Expenses (legacy)',
  '/schedule-test-drive': 'Schedule Test Drive',
  '/vehicle-interest': 'Vehicle Interest',
  '/dealings-register': 'Dealings Register',
  '/log-of-changes': 'Log of Changes',
  '/registration-transfer': 'Registration Transfer',
  '/registration-renewal': 'Registration Renewal',
  '/bulk-pricing-update': 'Bulk Pricing Update',
  '/dealer-licences': 'Dealer Licences',
  '/subscription': 'Subscription',
  '/bank-accounts': 'Bank Accounts',
  '/contacts': 'Contacts',
  '/toll-notice': 'Toll Notice',
  '/government-payments': 'Government Payments',
  '/purchase-orders': 'Purchase Orders',
  '/purchase-orders/add': 'New Purchase Order',
  '/credits': 'Credits',
  '/parts-finder': 'Parts Finder',
  '/sales-tools': 'Sales Tools',
  '/autograb-test': 'Autograb Test',
  '/autograb': 'Autograb Integration',
  '/easycars-documentation': 'EasyCars Documentation',
  '/admin/backups': 'Database Backups',
  '/quotations': 'Quotations',
  '/operations': 'Operations',
  '/finance': 'Finance',
  '/system': 'System Administration',
};

const getRouteTitle = (path: string): string => {
  // Exact match first
  if (routeTitles[path]) return routeTitles[path];
  
  // Handle dynamic routes
  if (path.startsWith('/inventory/')) {
    if (path.includes('/edit')) return 'Edit Vehicle';
    return 'Vehicle Details';
  }
  if (path.startsWith('/customers/')) {
    if (path.includes('/edit')) return 'Edit Customer';
    return 'Customer Details';
  }
  if (path.startsWith('/sales/')) {
    if (path.includes('/edit')) return 'Edit Deal';
    return 'Deal Details';
  }
  if (path.startsWith('/workshop/')) {
    if (path.includes('/edit')) return 'Edit Job';
    return 'Job Details';
  }
  if (path.startsWith('/tasks/')) {
    if (path.includes('/edit')) return 'Edit Task';
    return 'Task Details';
  }
  if (path.startsWith('/leads/')) {
    if (path.includes('/edit')) return 'Edit Lead';
    return 'Lead Details';
  }
  if (path.startsWith('/quotations/')) {
    if (path.includes('/edit')) return 'Edit Quotation';
    return 'Quotation Details';
  }
  if (path.startsWith('/purchase-orders/')) {
    return 'Purchase Order';
  }
  if (path.startsWith('/contacts/')) {
    return 'Contact Details';
  }

  // Default fallback — but if the last segment is a UUID, show a sane label instead
  const segments = path.split('/').filter(Boolean);
  const last = segments[segments.length - 1] || '';
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(last);
  if (isUuid && segments.length > 1) {
    // Render parent segment as title (e.g. "/foo/<uuid>" → "Foo Details")
    const parent = segments[segments.length - 2];
    const parentTitle = parent.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${parentTitle} Details`;
  }
  return last.charAt(0).toUpperCase() + last.slice(1) || 'Unknown';
};

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [navState, setNavState] = useState<NavigationState>({
    history: [],
    currentIndex: -1,
  });
  
  const [pageStates, setPageStates] = useState<Map<string, any>>(new Map());

  // Initialize with current page on mount
  useEffect(() => {
    const title = getRouteTitle(location.pathname);
    setNavState(prev => ({
      history: [{ path: location.pathname, title, timestamp: Date.now() }],
      currentIndex: 0,
    }));
  }, []);

  // Listen for navigation changes
  useEffect(() => {
    if (navState.history.length === 0) return;
    
    const current = navState.history[navState.currentIndex];
    if (current?.path !== location.pathname) {
      const title = getRouteTitle(location.pathname);
      
      setNavState(prev => {
        const newHistory = prev.history.slice(0, prev.currentIndex + 1);
        newHistory.push({ path: location.pathname, title, timestamp: Date.now() });
        
        // Limit history to 50 entries
        if (newHistory.length > 50) {
          newHistory.shift();
        }
        
        return {
          history: newHistory,
          currentIndex: newHistory.length - 1,
        };
      });
    }
  }, [location.pathname]);

  const navigateWithHistory = (path: string, title?: string, state?: any) => {
    const routeTitle = title || getRouteTitle(path);
    navigate(path, { state });

    setNavState(prev => {
      const newHistory = prev.history.slice(0, prev.currentIndex + 1);
      newHistory.push({ path, title: routeTitle, timestamp: Date.now(), state });

      if (newHistory.length > 50) {
        newHistory.shift();
      }

      return {
        history: newHistory,
        currentIndex: newHistory.length - 1,
      };
    });
  };

  const createNavigationHandler = (path: string, title?: string, state?: any) => {
    return (e: React.MouseEvent) => {
      // Middle click (button 1) or Ctrl+click or Cmd+click - open in new tab
      if (e.button === 1 || e.ctrlKey || e.metaKey) {
        window.open(path, '_blank');
        e.preventDefault();
        e.stopPropagation();
      } else if (e.button === 0) {
        // Left click - normal SPA navigation
        const routeTitle = title || getRouteTitle(path);
        navigateWithHistory(path, routeTitle, state);
      }
    };
  };

  const goBack = () => {
    if (navState.currentIndex > 0) {
      const previousEntry = navState.history[navState.currentIndex - 1];
      navigate(previousEntry.path, { state: previousEntry.state });
      
      setNavState(prev => ({
        ...prev,
        currentIndex: prev.currentIndex - 1,
      }));
    }
  };

  const canGoBack = navState.currentIndex > 0;

  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: Array<{ label: string; path?: string }> = [
      { label: 'Home', path: '/' }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === pathSegments.length - 1;
      
      // Handle dynamic routes
      let label = getRouteTitle(currentPath);
      if (label === segment.charAt(0).toUpperCase() + segment.slice(1)) {
        // If it's just the capitalized segment, use a more readable format
        label = segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      
      breadcrumbs.push({
        label,
        path: isLast ? undefined : currentPath,
      });
    });

    return breadcrumbs;
  };

  const preservePageState = (key: string, state: any) => {
    setPageStates(prev => new Map(prev.set(key, {
      data: state,
      timestamp: Date.now(),
      path: location.pathname,
    })));
  };

  const getPageState = (key: string) => {
    const state = pageStates.get(key);
    if (!state) return null;
    
    // Check if state is from the same path and not too old (5 minutes)
    const isExpired = Date.now() - state.timestamp > 300000;
    if (isExpired || state.path !== location.pathname) {
      pageStates.delete(key);
      return null;
    }
    
    return state.data;
  };

  const clearPageState = (key?: string) => {
    if (key) {
      setPageStates(prev => {
        const newMap = new Map(prev);
        newMap.delete(key);
        return newMap;
      });
    } else {
      setPageStates(new Map());
    }
  };

  return (
    <NavigationContext.Provider value={{
      navigateWithHistory,
      createNavigationHandler,
      goBack,
      canGoBack,
      getBreadcrumbs,
      preservePageState,
      getPageState,
      clearPageState,
    }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

// Safer navigation hook that falls back to regular navigate
export const useNavigationSafe = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    // Return a fallback with basic navigation functions
    const fallbackNavigate = useNavigate();
    return {
      navigateWithHistory: fallbackNavigate,
      createNavigationHandler: (path: string) => (e: React.MouseEvent) => {
        if (e.button === 1 || e.ctrlKey || e.metaKey) {
          window.open(path, '_blank');
          e.preventDefault();
          e.stopPropagation();
        } else if (e.button === 0) {
          fallbackNavigate(path);
        }
      },
      goBack: () => {},
      canGoBack: false,
      getBreadcrumbs: () => [{ label: 'Home', path: '/' }],
      preservePageState: () => {},
      getPageState: () => null,
      clearPageState: () => {},
    };
  }
  return context;
};

// Breadcrumb component
export const AppBreadcrumbs: React.FC = () => {
  const { getBreadcrumbs, navigateWithHistory } = useNavigation();
  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length <= 1) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Breadcrumbs
        separator={<Typography sx={{ fontSize: '0.875rem', mx: 0.5, color: 'text.secondary' }}>›</Typography>}
        aria-label="breadcrumb"
        sx={{
          display: 'flex',
          alignItems: 'center',
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap'
          },
          '& .MuiBreadcrumbs-separator': {
            mx: 0.5
          }
        }}
      >
        {breadcrumbs.map((breadcrumb, index) => {
          const isLast = index === breadcrumbs.length - 1;

          return isLast ? (
            <Typography
              key={breadcrumb.path || index}
              color="primary.main"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {breadcrumb.label}
            </Typography>
          ) : (
            <Link
              key={breadcrumb.path || index}
              color="text.secondary"
              href={breadcrumb.path}
              onClick={(e) => {
                e.preventDefault();
                if (breadcrumb.path) {
                  navigateWithHistory(breadcrumb.path, breadcrumb.label);
                }
              }}
              sx={{
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'text.primary'
                }
              }}
            >
              {breadcrumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

// Smart Back Button Component
export const SmartBackButton: React.FC<{ fallback?: string }> = ({ fallback = '/' }) => {
  const { goBack, canGoBack } = useNavigation();

  if (!canGoBack) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <button
        onClick={goBack}
        style={{
          background: 'none',
          border: 'none',
          color: 'primary.main',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '14px',
          padding: '4px 8px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        ← Back
      </button>
    </Box>
  );
};